import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Button,
  CircularProgress, Alert, Paper, AppBar, Toolbar, Avatar,
  Snackbar, Tabs, Tab, Collapse,
} from '@mui/material';
import {
  ArrowBack, Edit, Refresh, ContentCopy, Logout,
  FileDownload, ChevronLeft, ChevronRight, Person,
  ExpandMore, ExpandLess, OpenInNew,
  Info as InfoIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useIsEditor } from '../hooks/useIsEditor';
import TagsManagement from './TagsManagement';
import ContactsManagement from './ContactsManagement';
import ApproversManagement from './ApproversManagement';
import ExchangeMailboxInfo from './ExchangeMailboxInfo';
import AccountAttributesInfo from './AccountAttributesInfo';
import AuditLogTab from './AuditLogTab';
import GovCloudNotAvailable from './GovCloudNotAvailable';
import packageJson from '../../package.json';

const COMMERCIAL_API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://your-api-gateway-url/prod';
const GOVCLOUD_API_ENDPOINT = process.env.REACT_APP_GOVCLOUD_API_ENDPOINT || '';
const APP_VERSION = packageJson.version;

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  meta:   { fontSize: '0.8rem' },
  body:   { fontSize: '1rem' },
  title:  { fontSize: '1.25rem', fontWeight: 700, color: '#444444' },
  button: { fontSize: '1rem', textTransform: 'none' },
};

// Section avatar/accent colors
const COLORS = {
  awsOrg:     '#1976d2',
  servicenow: '#00796b',
  exchange:   '#e65100',
  notif:      '#6a1b9a',
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ─── Shared primitives ────────────────────────────────────────────────────────

const PersonRow = ({ name, color = COLORS.awsOrg }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
    <Avatar sx={{
      width: 34, height: 34, fontSize: '0.95rem', fontWeight: 700,
      letterSpacing: '1px', textIndent: '1px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: color, flexShrink: 0,
    }}>
      {getInitials(name)}
    </Avatar>
    <Typography sx={{ ...T.body, lineHeight: 1.3 }}>{name || '—'}</Typography>
  </Box>
);

const FieldRow = ({ label, value, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography sx={{ ...T.body, fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 140 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
      <Typography sx={{ ...T.body, color: 'text.secondary', wordBreak: 'break-all' }}>
        {value || '—'}
      </Typography>
      {action}
    </Box>
  </Box>
);

const CollapsibleCard = ({ icon, title, action, children, defaultExpanded = true, sx }) => {
  const [open, setOpen] = useState(defaultExpanded);
  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', ...sx }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2, cursor: 'pointer', userSelect: 'none',
          borderBottom: open ? '1px solid' : 'none', borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {React.cloneElement(icon, { sx: { fontSize: 28, color: '#444444' } })}
          <Typography sx={{ ...T.title }}>{title}</Typography>
          {action && <Box onClick={e => e.stopPropagation()}>{action}</Box>}
        </Box>
        {open
          ? <ExpandLess sx={{ fontSize: 20, color: 'text.secondary' }} />
          : <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 3 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AccountDetailPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, oktaAuth } = useOktaAuth();
  const isAdmin = useIsAdmin();
  const isEditor = useIsEditor();
  const canEdit = isAdmin || isEditor;

  const accountIds = location.state?.accountIds || [];
  const currentIndex = location.state?.currentIndex ?? accountIds.indexOf(accountId);
  const partition = location.state?.partition || 'commercial';
  const isGovCloud = partition === 'govcloud';

  // Resolve API endpoint based on partition
  const API_ENDPOINT = isGovCloud && GOVCLOUD_API_ENDPOINT
    ? GOVCLOUD_API_ENDPOINT
    : COMMERCIAL_API_ENDPOINT;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < accountIds.length - 1;

  const navigateTo = (idx) => {
    navigate('/accounts/' + accountIds[idx], { state: { accountIds, currentIndex: idx, partition } });
  };

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [approvers, setApprovers] = useState([]);
  const [approversLoading, setApproversLoading] = useState(false);
  const [approversRefreshing, setApproversRefreshing] = useState(false);
  const [approversCached, setApproversCached] = useState(false);

  // ServiceNow ticket state (fetched from S3 via backend)
  const [snowTicket, setSnowTicket] = useState(null);      // null = not yet loaded
  const [snowTicketLoading, setSnowTicketLoading] = useState(false);
  const [snowTicketError, setSnowTicketError] = useState(false);

  const [exchangeData, setExchangeData] = useState(null);
  const [attributesData, setAttributesData] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  const [tagsManagementOpen, setTagsManagementOpen] = useState(false);
  const [contactsManagementOpen, setContactsManagementOpen] = useState(false);
  const [approversManagementOpen, setApproversManagementOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarMessage, setCopySnackbarMessage] = useState('');

  const authenticatedFetch = useCallback(async (url, options = {}) => {
    const headers = { 'Content-Type': 'application/json' };
    if (authState?.accessToken?.accessToken) {
      headers['Authorization'] = `Bearer ${authState.accessToken.accessToken}`;
    }
    return fetch(url, { ...options, headers: { ...headers, ...(options.headers || {}) } });
  }, [authState]);

  const loadAccount = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Always fetch live from Organizations for account detail — ensures freshness after edits
      const url = `${API_ENDPOINT}/accounts/${accountId}?refresh=true&_=${Date.now()}`;
      const response = await authenticatedFetch(url);
      if (response.status === 404) { setError('Account not found.'); return; }
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setAccount(data);
    } catch (e) {
      setError(e.message || 'Failed to load account.');
    } finally {
      setLoading(false);
    }
  }, [accountId, authenticatedFetch]);

  const loadApprovers = useCallback(async () => {
    setApproversLoading(true);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINT}/accounts/${accountId}/approvers`);
      const data = await response.json();
      setApprovers(data.approvers || []);
    } catch (e) { /* non-fatal */ } finally {
      setApproversLoading(false);
    }
  }, [accountId, authenticatedFetch]);

  // Load account attributes directly so account_type is available for the AWS Orgs card header
  const loadAttributes = useCallback(async () => {
    try {
      const res = await authenticatedFetch(`${API_ENDPOINT}/accounts/${accountId}/attributes`);
      if (!res.ok) return;
      const data = await res.json();
      setAttributesData(data);
    } catch { /* non-fatal */ }
  }, [accountId, authenticatedFetch]);

  const loadSnowTicket = useCallback(async () => {
    setSnowTicketLoading(true);
    setSnowTicketError(false);
    try {
      const res = await authenticatedFetch(`${API_ENDPOINT}/accounts/${accountId}/snow-ticket`);
      if (res.status === 404) {
        setSnowTicket({ found: false });
        return;
      }
      if (!res.ok) {
        setSnowTicketError(true);
        return;
      }
      const data = await res.json();
      setSnowTicket(data);
    } catch {
      setSnowTicketError(true);
    } finally {
      setSnowTicketLoading(false);
    }
  }, [accountId, authenticatedFetch]);

  const refreshApprovers = useCallback(async () => {
    setApproversRefreshing(true);
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${accountId}/approvers?refresh=true&_=${Date.now()}`
      );
      const data = await response.json();
      setApprovers(data.approvers || []);
      setApproversCached(true);
      setTimeout(() => setApproversCached(false), 5000);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to refresh approvers', severity: 'error' });
    } finally {
      setApproversRefreshing(false);
    }
  }, [accountId, authenticatedFetch]);

  const copyToClipboard = useCallback((text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySnackbarMessage(`${label} copied`);
      setCopySnackbarOpen(true);
    });
  }, []);

  useEffect(() => { loadAccount(); }, [loadAccount]);
  useEffect(() => { loadApprovers(); }, [loadApprovers]);
  useEffect(() => { loadSnowTicket(); }, [loadSnowTicket]);
  useEffect(() => { loadAttributes(); }, [loadAttributes]);

  const handleExport = useCallback(() => {
    if (!account) return;
    const exportData = {
      id: account.id, name: account.name, email: account.email,
      status: account.status, ouPath: account.ouPath,
      joinedMethod: account.joinedMethod, joinedTimestamp: account.joinedTimestamp,
      tags: account.tags, approvers, exchange: exchangeData, attributes: attributesData,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${account.name}-${account.id}.json`; a.click();
    URL.revokeObjectURL(url);
  }, [account, approvers, exchangeData, attributesData]);

  const statusColor = account?.status === 'ACTIVE' ? 'success' : 'error';
  const tc = account?.tags?.technicalContact;
  const tcContacts = tc ? tc.split(/[\n+,]/).map(c => c.trim()).filter(Boolean) : [];

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading account...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 480, width: '100%' }}
          action={<Box sx={{ display: 'flex', gap: 1 }}><Button size="small" onClick={loadAccount}>Retry</Button><Button size="small" onClick={() => navigate('/')}>Back</Button></Box>}>
          {error}
        </Alert>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── AppBar ── */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {/* Verisk logo + app name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <img src="/verisk_logo_white.svg" alt="Verisk Logo" style={{ height: 32, marginRight: 12 }} />
            <Box sx={{ borderLeft: '1px solid rgba(255,255,255,0.3)', height: 24, mr: 1 }} />
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 400, letterSpacing: '0.5px' }}>
              Orion Portal
            </Typography>
          </Box>

          {/* Vertical rule + account name */}
          <Box sx={{ width: '1px', height: 32, bgcolor: 'rgba(255,255,255,0.2)', mx: 1.5, flexShrink: 0 }} />
          <Box sx={{
            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1,
            '& .copy-icon': { opacity: 0, transition: 'opacity 0.15s' },
            '&:hover .copy-icon': { opacity: 1 },
          }}>
            <Typography noWrap sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 500, letterSpacing: '0.3px' }}>
              {account?.name || accountId}
            </Typography>
            {account?.name && (
              <Tooltip title="Copy account name">
                <IconButton className="copy-icon" size="small" sx={{ color: 'rgba(255,255,255,0.7)', p: 0.25, flexShrink: 0 }}
                  onClick={() => copyToClipboard(account.name, 'Account name')}>
                  <ContentCopy sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {isGovCloud && (
              <Chip
                label="GovCloud"
                size="small"
                sx={{
                  height: 20, fontSize: '0.7rem', fontWeight: 700,
                  bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', flexShrink: 0,
                }}
              />
            )}
          </Box>

          {/* Right actions */}
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={() => oktaAuth.signOut()}><Logout /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* ── Nav strip ── */}
      <Box sx={(theme) => ({
        position: 'fixed', top: 64, left: 0, right: 0,
        zIndex: theme.zIndex.appBar - 1,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 2.5, height: 44,
        display: 'flex', alignItems: 'center', gap: 1,
      })}>
        <Tooltip title="Click to go back" arrow>
          <Button size="small" startIcon={<ArrowBack sx={{ fontSize: 22 }} />}
            onClick={() => navigate('/')}
            sx={{ fontSize: '1.25rem', fontWeight: 500, color: 'primary.main', textTransform: 'none', minWidth: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
            Accounts
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" size="small"
          startIcon={<FileDownload sx={{ fontSize: 14 }} />}
          disabled={!account} onClick={handleExport}
          sx={{ ...T.meta, textTransform: 'none', py: 0.3, px: 1.25, whiteSpace: 'nowrap' }}>
          Export
        </Button>
        {accountIds.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={hasPrev ? `Previous (${currentIndex} of ${accountIds.length})` : 'No previous account'}>
              <span>
                <IconButton size="small" disabled={!hasPrev} onClick={() => navigateTo(currentIndex - 1)}>
                  <ChevronLeft sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Typography sx={{ ...T.meta, color: 'text.secondary', px: 0.5 }}>
              {currentIndex + 1} / {accountIds.length}
            </Typography>
            <Tooltip title={hasNext ? `Next (${currentIndex + 2} of ${accountIds.length})` : 'No next account'}>
              <span>
                <IconButton size="small" disabled={!hasNext} onClick={() => navigateTo(currentIndex + 1)}>
                  <ChevronRight sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* ── Tab bar (admin only) — sits below nav strip, above content ── */}
      {isAdmin && (
        <Box sx={(theme) => ({
          position: 'fixed', top: 108, left: 0, right: 0,
          zIndex: theme.zIndex.appBar - 2,
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: 2.5,
        })}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, fontSize: '0.95rem', textTransform: 'none', py: 0.5 },
            }}
          >
            <Tab label="Overview" value="info" />
            <Tab label="Audit Log" value="audit" />
          </Tabs>
        </Box>
      )}

      {/* ── Page content ── */}
      <Box sx={(theme) => ({
        bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f0f2f5',
        minHeight: '100vh',
        pt: isAdmin ? '156px' : '116px',
        pb: 4, px: 3,
      })}>

        {/* ── Overview tab ── */}
        {activeTab === 'info' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* AWS Organizations */}
            <CollapsibleCard icon={<InfoIcon />} title="AWS Organizations">
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'stretch' }}>

                {/* Left: account fields */}
                <Box sx={{ pr: 3 }}>
                  <FieldRow label="Account ID" value={account?.id}
                    action={account?.id && (
                      <Box sx={{ '& .copy-icon': { opacity: 0, transition: 'opacity 0.15s' }, '&:hover .copy-icon': { opacity: 1 } }}>
                        <Tooltip title="Copy Account ID">
                          <IconButton className="copy-icon" size="small" sx={{ p: 0.25 }}
                            onClick={() => copyToClipboard(account.id, 'Account ID')}>
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  />
                  {attributesData?.account_type && (
                    <FieldRow label="Account Type" value={
                      <Chip
                        label={attributesData.account_type}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.75rem', fontWeight: 700, color: '#fff',
                          bgcolor: (() => {
                            const t = (attributesData.account_type || '').toLowerCase();
                            if (t === 'managed') return '#2e7d32';
                            if (t === 'acquisition') return '#e65100';
                            if (t.includes('shared')) return '#00796b';
                            if (t === 'sandbox') return '#6a1b9a';
                            return '#757575';
                          })(),
                        }}
                      />
                    } />
                  )}
                  {attributesData?.account_type?.toLowerCase() === 'managed' && attributesData?.account_create_time && (() => {
                    let d = null;
                    const raw = attributesData.account_create_time;
                    try {
                      const parts = raw.split('-');
                      if (parts.length >= 3 && raw.includes('T')) {
                        d = new Date(raw);
                      } else if (parts.length >= 3) {
                        const year = parseInt(parts[0]);
                        const p1 = parseInt(parts[1]);
                        const p2 = parseInt(parts[2]);
                        const month = p1 > 12 ? p2 - 1 : p1 - 1;
                        const day = p1 > 12 ? p1 : p2;
                        const hour = parts[3] ? parseInt(parts[3]) : 0;
                        const min = parts[4] ? parseInt(parts[4]) : 0;
                        d = new Date(year, month, day, hour, min);
                      }
                    } catch { d = null; }
                    if (!d || isNaN(d.getTime())) return null;
                    const formatted = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    return <FieldRow label="Account Created" value={formatted} />;
                  })()}
                  <FieldRow label="Status" value={
                    account?.status && (
                      <Chip label={account.status} color={statusColor} size="small"
                        sx={{ height: 20, fontSize: '0.75rem', fontWeight: 900 }} />
                    )
                  } />
                  <FieldRow label="OU Path" value={account?.ouPath} />
                  <FieldRow label="Email" value={account?.email} />
                  <FieldRow label="Joined Date"
                    value={account?.joinedTimestamp
                      ? new Date(account.joinedTimestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : null}
                  />
                  {account?.joinedMethod && (
                    <FieldRow label="Origin" value={
                      <Chip
                        label={account.joinedMethod}
                        size="small"
                        sx={{
                          height: 20, fontSize: '0.75rem', fontWeight: 700, color: '#fff',
                          bgcolor: account.joinedMethod?.toUpperCase() === 'INVITED' ? '#e65100' : '#1565c0',
                        }}
                      />
                    } />
                  )}
                  <FieldRow label="Functional Unit" value={account?.tags?.functionalUnit}
                    action={canEdit && (
                      <Tooltip title="Edit tags">
                        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setTagsManagementOpen(true)}>
                          <Edit sx={{ fontSize: 24, color: 'action.active' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                  <FieldRow label="Business Unit" value={account?.tags?.businessUnit} />
                </Box>

                {/* Right: contacts */}
                <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 3 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start' }}>
                    {/* Account Owner */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ ...T.title }}>Account Owner</Typography>
                        {canEdit && (
                          <Tooltip title="Edit contacts">
                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => setContactsManagementOpen(true)}>
                              <Edit sx={{ fontSize: 20, color: 'action.active' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {account?.tags?.accountOwner
                        ? <PersonRow name={account.tags.accountOwner} color={COLORS.awsOrg} />
                        : <Typography sx={{ ...T.body, color: 'text.secondary' }}>—</Typography>
                      }
                    </Box>
                    {/* Technical Contact */}
                    <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
                      <Typography sx={{ ...T.title, mb: 0.5 }}>Technical Contact</Typography>
                      {tcContacts.length === 0
                        ? <Typography sx={{ ...T.body, color: 'text.secondary' }}>—</Typography>
                        : tcContacts.map((c, i) => <PersonRow key={i} name={c} color="#6a1b9a" />)
                      }
                    </Box>
                  </Box>
                </Box>

              </Box>
            </CollapsibleCard>

            {/* ServiceNow + Exchange Online — side by side */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'stretch' }}>

              {/* ServiceNow */}
              <CollapsibleCard
                icon={<SecurityIcon />}
                title="ServiceNow"
                action={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {approvers.length > 0 && (
                      <Chip label={`${approvers.length} approvers`} size="small" sx={{ height: 20, ...T.meta }} />
                    )}
                    {approversCached && (
                      <Chip label="refreshed ✓" size="small" color="success" variant="outlined" sx={{ height: 18, ...T.meta }} />
                    )}
                    <Tooltip title="Refresh approvers">
                      <IconButton size="small" onClick={refreshApprovers} disabled={approversRefreshing} sx={{ p: 0.25 }}>
                        {approversRefreshing ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                      <Tooltip title="Edit approvers">
                        <IconButton size="small" onClick={() => setApproversManagementOpen(true)} sx={{ p: 0.25 }}>
                          <Edit sx={{ fontSize: 24, color: 'action.active' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* ServiceNow Ticket link — commercial only */}
                    {!isGovCloud && !snowTicketLoading && !snowTicketError && snowTicket?.found === true && snowTicket.ritm && (
                      <Box sx={{
                        ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.25,
                        '& .copy-icon': { opacity: 0, transition: 'opacity 0.15s' },
                        '&:hover .copy-icon': { opacity: 1 },
                      }}>
                        <Tooltip title="Open ServiceNow ticket">
                          <Chip
                            label={snowTicket.ritm}
                            size="small"
                            icon={<OpenInNew sx={{ fontSize: '0.75rem !important' }} />}
                            onClick={() => {
                              if (snowTicket.ritm_sys_id) {
                                window.open(
                                  `https://oneverisk.service-now.com/now/nav/ui/classic/params/target/sc_req_item.do%3Fsys_id%3D${snowTicket.ritm_sys_id}%26sysparm_stack%3D%26sysparm_view%3D`,
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              }
                            }}
                            sx={{
                              height: 22,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: 'primary.main',
                              color: '#fff',
                              cursor: snowTicket.ritm_sys_id ? 'pointer' : 'default',
                              '& .MuiChip-icon': { color: '#fff' },
                              '&:hover': snowTicket.ritm_sys_id ? { bgcolor: 'primary.dark' } : {},
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Copy RITM number">
                          <IconButton
                            className="copy-icon"
                            size="small"
                            sx={{ p: 0.25, color: 'text.secondary' }}
                            onClick={() => copyToClipboard(snowTicket.ritm, 'RITM number')}
                          >
                            <ContentCopy sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                }
              >
                {approversLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography sx={{ ...T.body, color: 'text.secondary' }}>Loading...</Typography>
                  </Box>
                ) : approvers.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, color: 'text.disabled' }}>
                    <Person sx={{ fontSize: 32, mb: 0.5 }} />
                    <Typography sx={{ ...T.body }}>No approvers configured</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 24px' }}>
                    {approvers.map((approver, idx) => {
                      const name = typeof approver === 'string' ? approver : (approver.name || approver.user_name || '');
                      return <PersonRow key={idx} name={name} color={COLORS.servicenow} />;
                    })}
                  </Box>
                )}
              </CollapsibleCard>

              {/* Exchange Online */}
              <CollapsibleCard icon={<EmailIcon />} title="Exchange Online">
                <ExchangeMailboxInfo
                  account={account}
                  authenticatedFetch={authenticatedFetch}
                  API_ENDPOINT={API_ENDPOINT}
                  isAdmin={isAdmin}
                  onDataLoaded={setExchangeData}
                />
              </CollapsibleCard>

            </Box>

            {/* Notifications & Reports */}
            <CollapsibleCard icon={<NotificationsIcon />} title="Notifications & Reports">
              <AccountAttributesInfo
                account={account}
                authenticatedFetch={authenticatedFetch}
                API_ENDPOINT={API_ENDPOINT}
                canEdit={canEdit}
                onDataLoaded={setAttributesData}
              />
            </CollapsibleCard>

          </Box>
        )}

        {/* ── Audit Log tab ── */}
        {activeTab === 'audit' && isAdmin && (
          <AuditLogTab
            account={account}
            authenticatedFetch={authenticatedFetch}
            API_ENDPOINT={API_ENDPOINT}
          />
        )}

      </Box>

      {/* ── Dialogs ── */}
      {tagsManagementOpen && (
        <TagsManagement open={tagsManagementOpen} onClose={() => setTagsManagementOpen(false)}
          accounts={[account]}
          onUpdate={() => { loadAccount(); setTagsManagementOpen(false); setSnackbar({ open: true, message: 'Tags updated', severity: 'success' }); }}
          authenticatedFetch={authenticatedFetch} API_ENDPOINT={API_ENDPOINT} />
      )}
      {contactsManagementOpen && (
        <ContactsManagement open={contactsManagementOpen} onClose={() => setContactsManagementOpen(false)}
          account={account}
          onUpdate={() => { loadAccount(); setContactsManagementOpen(false); setSnackbar({ open: true, message: 'Contacts updated', severity: 'success' }); }}
          authenticatedFetch={authenticatedFetch} API_ENDPOINT={API_ENDPOINT} />
      )}
      {approversManagementOpen && (
        <ApproversManagement open={approversManagementOpen} onClose={() => setApproversManagementOpen(false)}
          account={account}
          onUpdate={() => { refreshApprovers(); setSnackbar({ open: true, message: 'Approvers updated', severity: 'success' }); }}
          authenticatedFetch={authenticatedFetch} API_ENDPOINT={API_ENDPOINT} />
      )}

      {/* ── Snackbars ── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar open={copySnackbarOpen} autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)} message={copySnackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

      {/* ── Version badge ── */}
      <Box sx={{
        position: 'fixed', bottom: 0, right: 0, p: 1,
        bgcolor: 'rgba(0,0,0,0.05)', borderTopLeftRadius: 8,
      }}>
        <Typography variant="caption" color="text.secondary">v{APP_VERSION}</Typography>
      </Box>
    </>
  );
};

export default AccountDetailPage;
